import { Components, ThemeOptions, createTheme } from "@mui/material/styles";
import { formLabelClasses, inputLabelClasses } from "@mui/material";
// import { Theme } from "@emotion/react";
// import { TypeObject } from "@mui/material/styles/createPalette";

declare module "@mui/material/styles" {
    interface Components {
        MuiLoadingButton?: {
            styleOverrides?: {
                root?: React.CSSProperties,
                loadingIndicator?: React.CSSProperties,
                startIconLoading?: React.CSSProperties,
                endIconLoading?: React.CSSProperties,
            },
        };
    }

    interface TypeBackground {
        card?: string;
        secondaryCard?: string;
        input?: string;
    }
}

const componentsConfig: Components<ThemeOptions> = {
    MuiCssBaseline: {
        styleOverrides: (theme) => ({
            'input[type="date"], input[type="time"], input[type="datetime-local"], input[type="month"], input[type="week"]':
                {
                    colorScheme: theme.palette?.mode === "dark" ? "dark" : "light",
                },
        }),
    },
    MuiPaper: {
        variants: [
            {
                props: {},
                style: {
                    borderRadius: "12px",
                },
            },
        ],
    },
    MuiInputLabel: {
        variants: [
            {
                props: { size: "small" }, // When size is 'small'
                style: {
                    transform: "translate(20px, 9px) scale(1) !important",
                    [`&.${formLabelClasses.focused}`]: {
                        transform: "translate(22px, -9px) scale(0.75) !important",
                    },
                    [`&.${formLabelClasses.filled}`]: {
                        transform: "translate(22px, -9px) scale(0.75) !important",
                    },
                    [`&.${inputLabelClasses.shrink}`]: {
                        transform: "translate(22px, -9px) scale(0.75) !important",
                    },
                },
            },
            {
                props: {},
                style: {
                    transform: "translate(22px, 17px) scale(1)",
                    [`&.${formLabelClasses.focused}`]: {
                        transform: "translate(22px, -9px) scale(0.75)",
                    },
                    [`&.${formLabelClasses.filled}`]: {
                        transform: "translate(22px, -9px) scale(0.75)",
                    },
                    [`&.${inputLabelClasses.shrink}`]: {
                        transform: "translate(22px, -9px) scale(0.75)",
                    },
                },
            },
        ],
    },
    MuiFormLabel: {
        styleOverrides: {
            asterisk: {
                color: "red",
                "&$error": {
                    color: "red",
                },
            },
        },
    },
    MuiSelect: {
        defaultProps: {
            classes: {
                filled: "selectBox-filled",
                icon: "selectBox-icon",
                iconFilled: "selectBox-iconFilled",
                iconOpen: "selectBox-iconOpen",
                iconOutlined: "selectBox-iconOutlined",
                iconStandard: "selectBox-iconStandard",
                multiple: "selectBox-multiple",
                nativeInput: "selectBox-nativeInput",
                outlined: "selectBox-outlined",
                root: "selectBox-root",
                select: "selectBox-select",
                standard: "selectBox-standard",
            },
        },
    },
    MuiOutlinedInput: {
        variants: [
            {
                props: {},
                style: {
                    borderRadius: 28,
                    padding: "0 8px",
                    legend: {
                        marginLeft: "6px",
                    },
                    textarea: {
                        padding: "16px 8px",
                    },
                },
            },
        ],
    },
    MuiLoadingButton: {
        styleOverrides: {
            loadingIndicator: {
                color: "#134294",
            },
        },
    },
    MuiButton: {
        variants: [
            {
                props: { variant: "contained", color: "success", size: "medium" },
                style: {
                    borderRadius: 22,
                    background: "linear-gradient(232.87deg, #4ec50a  -9.82%, #44ad09  106.44%)",
                    fontWeight: 600,
                    boxShadow: "0px 0px 32px 0px rgba(68, 173, 9, 0.3)",
                    lineHeight: "44px",
                    padding: "0 32px",
                    color: "#fff",
                    "&:hover": {
                        boxShadow: "0px 0px 16px 0px rgba(68, 173, 9, 0.3)",
                    },
                    "&:disabled": {
                        background: "rgba(231, 231, 231, 1)",
                        color: "rgba(153, 153, 153, 1)",
                        boxShadow: "unset",
                    },
                },
            },
            {
                props: { variant: "outlined", color: "success", size: "medium" },
                style: {
                    borderRadius: 22,
                    background: "transparent",
                    fontWeight: 600,
                    lineHeight: "44px",
                    padding: "0 32px",
                    color: "#44ad09",
                    "&:hover": {
                        boxShadow: "0px 0px 16px 0px rgba(68, 173, 9, 0.3)",
                    },
                    "&:disabled": {
                        background: "rgba(231, 231, 231, 1)",
                        color: "rgba(153, 153, 153, 1)",
                        boxShadow: "unset",
                    },
                },
            },
            {
                props: { variant: "outlined", color: "primary", size: "medium" },
                style: {
                    borderRadius: 22,
                    background: "transparent",
                    fontWeight: 600,
                    lineHeight: "44px",
                    padding: "0 32px",
                    "&:hover": {
                        boxShadow: "0px 0px 16px 0px rgba(15, 98, 243, 0.3)",
                    },
                    "&:disabled": {
                        background: "rgba(231, 231, 231, 1)",
                        color: "rgba(153, 153, 153, 1)",
                        boxShadow: "unset",
                    },
                },
            },
            {
                props: { variant: "contained", color: "primary", size: "medium" },
                style: {
                    borderRadius: 22,
                    background: "linear-gradient(232.87deg, #0F62F3 -9.82%, #09398D 106.44%)",
                    fontWeight: 600,
                    boxShadow: "0px 0px 32px 0px rgba(15, 98, 243, 0.3)",
                    lineHeight: "44px",
                    padding: "0 32px",
                    "&:hover": {
                        boxShadow: "0px 0px 16px 0px rgba(15, 98, 243, 0.3)",
                    },
                    "&:disabled": {
                        background: "rgba(231, 231, 231, 1)",
                        color: "rgba(153, 153, 153, 1)",
                        boxShadow: "unset",
                    },
                },
            },
            {
                props: { variant: "contained", color: "primary", size: "small" },
                style: {
                    borderRadius: 22,
                    background: "linear-gradient(232.87deg, #0F62F3 -9.82%, #09398D 106.44%)",
                    fontWeight: 600,
                    boxShadow: "0px 0px 32px 0px rgba(15, 98, 243, 0.3)",
                    lineHeight: "24px",
                    padding: "0 12px",
                    "&:hover": {
                        boxShadow: "0px 0px 16px 0px rgba(15, 98, 243, 0.3)",
                    },
                    "&:disabled": {
                        background: "rgba(231, 231, 231, 1)",
                        color: "rgba(153, 153, 153, 1)",
                        boxShadow: "unset",
                    },
                },
            },
            {
                props: { variant: "contained", color: "error", size: "small" },
                style: {
                    borderRadius: 22,
                    background: "linear-gradient(232.87deg, #f30f0f -9.82%, #8d0909 106.44%)",
                    fontWeight: 600,
                    boxShadow: "0px 0px 32px 0px rgba(243, 15, 15, 0.3)",
                    lineHeight: "24px",
                    padding: "0 12px",
                    "&:hover": {
                        boxShadow: "0px 0px 16px 0px rgba(243, 15, 15, 0.3)",
                    },
                    "&:disabled": {
                        background: "rgba(231, 231, 231, 1)",
                        color: "rgba(153, 153, 153, 1)",
                        boxShadow: "unset",
                    },
                },
            },
            {
                props: { variant: "contained", color: "error", size: "medium" },
                style: {
                    borderRadius: 22,
                    background: "linear-gradient(232.87deg, #f30f0f -9.82%, #8d0909 106.44%)",
                    fontWeight: 600,
                    boxShadow: "0px 0px 32px 0px rgba(243, 15, 15, 0.3)",
                    lineHeight: "44px",
                    padding: "0 32px",
                    "&:hover": {
                        boxShadow: "0px 0px 16px 0px rgba(243, 15, 15, 0.3)",
                    },
                    "&:disabled": {
                        background: "rgba(231, 231, 231, 1)",
                        color: "rgba(153, 153, 153, 1)",
                        boxShadow: "unset",
                    },
                },
            },
            {
                props: { variant: "text", color: "primary" },
                style: {
                    borderRadius: 22,
                    background: "#fff",
                    fontWeight: 600,
                    lineHeight: "42px",
                    padding: "0 32px",
                    color: "rgba(33, 33, 33, 1)",
                    border: "1px solid rgba(213, 213, 213, 1)",
                    "&:hover": {
                        background: "#eaeaea",
                        color: "#333",
                    },
                },
            },
        ],
    },
    MuiTableContainer: {
        defaultProps: {
            classes: {
                root: "table-container",
            },
        },
    },
};

export const lightTheme = createTheme({
    components: componentsConfig,
    typography: {
        fontFamily: ["Raleway", "Noto", "sans-serif"].join(","),
    },
    shape: {
        borderRadius: 10,
    },
    palette: {
        mode: "light",
        background: {
            default: "#f1f1f1",
            card: "#fff",
            secondaryCard: "#ccc",
            input: "rgba(0, 0, 0, 0.23)",
        },
        primary: {
            main: "#298EF4",
        },
        secondary: {
            main: "#F3A012",
        },
        error: {
            main: "#FF515D",
        },
    },
});

export const darkTheme = createTheme({
    components: componentsConfig,
    typography: {
        fontFamily: ["Raleway", "Noto", "sans-serif"].join(","),
    },
    shape: {
        borderRadius: 10,
    },
    palette: {
        mode: "dark",

        background: {
            default: "#212121",
            card: "#313131",
            secondaryCard: "#444",
            input: "rgba(255, 255, 255, 0.23)",
        },
        primary: {
            main: "#298EF4",
        },
        secondary: {
            main: "#EE750D",
        },
        error: {
            main: "#FF515D",
        },
    },
});
